// ── Epics management page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, renderAvatarHtml, TYPE_COLORS, getTypeIcon } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { renderQuickCreate, bindQuickCreate, removeQuickCreate } from '../components/quick-create.js';
import { loadStatuses, statusLabel, statusBadgeAttrs, roleStatus } from '../lib/statuses.js';

const TYPE_LABELS = { story: 'Stories', task: 'Tasks', bug: 'Bugs' };

let state = {
  projectId: null,
  epics: [],
  members: [],
  statuses: [],
  expandedEpics: new Set(),
  childrenCache: new Map(),
};

function resetState() {
  state = {
    projectId: null,
    epics: [],
    members: [],
    statuses: [],
    expandedEpics: new Set(),
    childrenCache: new Map(),
  };
}

function epicBadge(status) {
  const attrs = statusBadgeAttrs(state.statuses.find(s => s.name === status));
  return `<span class="badge ${attrs.className}" style="${attrs.style}">${escapeHtml(statusLabel(status))}</span>`;
}

function findMember(assigneeId) {
  if (!assigneeId) return null;
  return state.members.find(m => m.user_id === assigneeId || m.id === assigneeId) || null;
}


function computeProgress(epic) {
  const children = state.childrenCache.get(epic.task_key);
  if (!children || children.length === 0) {
    return { completed: 0, total: epic.children_count || 0, points: epic.story_points || 0 };
  }
  const completeName = roleStatus(state.statuses, 'complete')?.name;
  const completed = children.filter(c => c.status === completeName).length;
  const points = children.reduce((sum, c) => sum + (c.story_points || 0), 0);
  return { completed, total: children.length, points };
}

// ── Render ──

function renderEpicCard(epic) {
  const expanded = state.expandedEpics.has(epic.task_key);
  const { completed, total, points } = computeProgress(epic);
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const member = findMember(epic.assignee);

  let html = `<div class="epic-card ${expanded ? 'epic-card-expanded' : ''}" data-epic-key="${escapeHtml(epic.task_key)}">`;

  // Header
  html += `<div class="epic-card-header">
    <a class="badge badge-epic task-key-link" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(epic.task_key)}" draggable="false" title="Open task page">${escapeHtml(epic.task_key)}</a>
    <div style="display:flex;align-items:center;gap:0.5rem;">
      ${epicBadge(epic.status)}
      ${member ? renderAvatarHtml(member, 24) : ''}
    </div>
  </div>`;

  // Title + desc
  html += `<div class="epic-card-title">${escapeHtml(epic.title)}</div>`;
  if (epic.description) {
    html += `<div class="epic-card-desc">${escapeHtml(epic.description)}</div>`;
  }

  // Meta: progress bar + stats
  html += `<div class="epic-card-meta">
    <div class="epic-progress"><div class="epic-progress-fill" style="width:${pct}%"></div></div>
    <span>${completed}/${total}</span>
    ${points > 0 ? `<span title="Story points">${points} SP</span>` : ''}
  </div>`;

  // Expanded children
  if (expanded) {
    const children = state.childrenCache.get(epic.task_key);
    html += renderChildren(epic, children);
  }

  html += `</div>`;
  return html;
}

function renderChildren(epic, children) {
  if (!children) {
    return `<div class="epic-children"><div class="empty"><span class="spinner"></span></div></div>`;
  }

  let html = `<div class="epic-children">`;

  if (children.length === 0) {
    html += `<div class="text-muted text-sm" style="padding:0.5rem 0;">No child items yet</div>`;
  } else {
    // Group by type
    const groups = {};
    for (const child of children) {
      const t = child.type || 'task';
      if (!groups[t]) groups[t] = [];
      groups[t].push(child);
    }

    for (const type of ['story', 'task', 'bug']) {
      const items = groups[type];
      if (!items || items.length === 0) continue;
      html += `<div class="epic-type-group">`;
      html += `<div class="epic-type-label">${TYPE_LABELS[type] || type}</div>`;
      for (const child of items) {
        const childMember = findMember(child.assignee);
        html += `<div class="epic-child-row" data-child-key="${escapeHtml(child.task_key)}">
          ${getTypeIcon(child.type, 14)}
          <a class="epic-child-key task-key-link" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(child.task_key)}" draggable="false" title="Open task page">${escapeHtml(child.task_key)}</a>
          <span class="epic-child-title">${escapeHtml(child.title)}</span>
          <span class="badge ${statusBadgeAttrs(state.statuses.find(s => s.name === child.status)).className}" style="${statusBadgeAttrs(state.statuses.find(s => s.name === child.status)).style};font-size:0.7rem;padding:0.1rem 0.35rem;">${escapeHtml(statusLabel(child.status))}</span>
          ${child.story_points ? `<span class="text-muted text-sm">${child.story_points} SP</span>` : ''}
          ${childMember ? renderAvatarHtml(childMember, 20) : ''}
        </div>`;
      }
      html += `</div>`;
    }
  }

  html += renderQuickCreate('epic-' + epic.task_key);
  html += `</div>`;
  return html;
}

function render() {
  const content = getContentEl();
  if (!content) return;

  let html = `<div class="page-header">
    <h2>Epics</h2>
    <button class="btn btn-primary" id="create-epic-btn">+ Create Epic</button>
  </div>`;

  if (state.epics.length === 0) {
    html += `<div class="empty text-muted">No epics yet. Create one to get started.</div>`;
  } else {
    html += `<div class="grid" id="epics-grid">`;
    for (const epic of state.epics) {
      html += renderEpicCard(epic);
    }
    html += `</div>`;
  }

  content.innerHTML = html;
}

// ── Expand/collapse ──

async function toggleExpand(taskKey) {
  if (state.expandedEpics.has(taskKey)) {
    state.expandedEpics.delete(taskKey);
    render();
    return;
  }

  state.expandedEpics.add(taskKey);
  render(); // show spinner

  if (!state.childrenCache.has(taskKey)) {
    try {
      const res = await api.get('/projects/' + state.projectId + '/tasks/' + taskKey + '/children');
      state.childrenCache.set(taskKey, res.children || []);
    } catch (err) {
      showToast('Failed to load children: ' + err.message, 'error');
      state.expandedEpics.delete(taskKey);
    }
  }

  render();
}

async function refreshEpic(taskKey) {
  try {
    const res = await api.get('/projects/' + state.projectId + '/tasks/' + taskKey + '/children');
    state.childrenCache.set(taskKey, res.children || []);
  } catch (_) {}
  render();
}

// ── Create modals ──

function openCreateEpicModal() {
  const body = `
    <form id="create-epic-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <div>
        <label class="form-label">Title</label>
        <input class="form-input" name="title" placeholder="Epic title" required>
      </div>
      <div>
        <label class="form-label">Description</label>
        <textarea class="form-textarea" name="description" rows="3" placeholder="Describe this epic..."></textarea>
      </div>
      <button type="submit" class="btn btn-primary">Create Epic</button>
    </form>`;

  openModal('Create Epic', body, {
    onMount(overlay) {
      overlay.querySelector('#create-epic-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;
        const data = {
          title: form.title.value.trim(),
          description: form.description.value.trim(),
          type: 'epic',
          category: 'CODING',
          status: roleStatus(state.statuses, 'start')?.name || 'pending',
        };
        if (!data.title) return;

        try {
          const res = await api.post('/projects/' + state.projectId + '/tasks', data);
          state.epics.push(res.task);
          closeModal();
          render();
          showToast('Epic created', 'success');
        } catch (err) {
          showToast(err.message || 'Failed to create epic', 'error');
        }
      });
    },
  });
}

// ── Event handler ──

function handleClick(e) {
  // Permalink click (epic key or child key) → let the anchor navigate natively.
  // stopPropagation() below on child rows stops propagation, not default action,
  // so without this the anchor would navigate AND openTaskDetail would fire.
  if (e.target.closest('a')) return;

  // Create epic btn
  if (e.target.closest('#create-epic-btn')) {
    openCreateEpicModal();
    return;
  }

  // Quick-create trigger/form — let bindQuickCreate's own handler manage it
  if (e.target.closest('.quick-create-wrap')) return;

  // Click child → open detail
  const childRow = e.target.closest('.epic-child-row');
  if (childRow) {
    e.stopPropagation();
    const childKey = childRow.dataset.childKey;
    const epicCard = childRow.closest('.epic-card');
    const epicKey = epicCard?.dataset.epicKey;
    const children = state.childrenCache.get(epicKey) || [];
    openTaskDetail(state.projectId, childKey, {
      allTasks: children,
      onClose: async () => {
        if (epicKey) await refreshEpic(epicKey);
      },
    });
    return;
  }

  // Click epic card → toggle expand
  const epicCard = e.target.closest('.epic-card');
  if (epicCard) {
    toggleExpand(epicCard.dataset.epicKey);
  }
}

// ── Page entry ──

export async function epicsPage(params) {
  if (!(await checkAuth())) return;

  resetState();
  state.projectId = params.id;

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'epics', viewLabel: 'Epics' });

  const content = getContentEl();
  content.innerHTML = `<div class="page-header"><h2>Epics</h2></div><div class="empty"><span class="spinner"></span></div>`;

  try {
    const [epicsRes, membersRes, statuses] = await Promise.all([
      api.get('/projects/' + params.id + '/tasks?type=epic'),
      api.get('/projects/' + params.id + '/members'),
      loadStatuses(params.id),
    ]);
    state.epics = epicsRes.tasks || [];
    state.members = membersRes.members || [];
    state.statuses = statuses || [];
  } catch (err) {
    content.innerHTML = `<div class="page-header"><h2>Epics</h2></div><div class="empty text-muted">Failed to load epics</div>`;
    return;
  }

  render();
  content.addEventListener('click', handleClick);
  bindQuickCreate(content, {
    projectId: state.projectId,
    getTasks: (triggerEl) => {
      // Flatten all cached children for key uniqueness
      const all = [];
      for (const children of state.childrenCache.values()) {
        all.push(...children);
      }
      // Include epics themselves
      all.push(...state.epics);
      return all;
    },
    onCreated: async (task) => {
      const epicKey = state.epics.find(e => e.id === task.parent_id)?.task_key;
      if (epicKey) {
        const idx = state.epics.findIndex(e => e.task_key === epicKey);
        if (idx >= 0) state.epics[idx].children_count = (state.epics[idx].children_count || 0) + 1;
        await refreshEpic(epicKey);
      }
    },
    getContext: (triggerEl) => {
      const epicCard = triggerEl.closest('.epic-card');
      const epicKey = epicCard?.dataset.epicKey;
      const epic = state.epics.find(e => e.task_key === epicKey);
      return { parentId: epic?.id, status: roleStatus(state.statuses, 'start')?.name || 'pending' };
    },
  });

  return () => {
    removeQuickCreate();
    content.removeEventListener('click', handleClick);
    closeTaskDetail();
    resetState();
  };
}
